import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SchedulePriority,
  ScheduleWhereInput,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { SelectOption } from 'src/components/general/inputs/select/select';
import { AppointmentsPageQueryParams } from 'src/components/pages/appointments/appointments.page';
import { AppointmentStatusResolver } from 'src/components/pages/appointments/shared';
import { useDateHelpers } from 'src/hooks/use-date-helpers';

export const useAppointmentsWhereInputResolver = (
  viewCode: AppointmentsPageQueryParams['view-code'],
) => {
  const { startOfDay, parseISO, initDayJsDate } = useDateHelpers();
  const nowDate = useMemo(
    () => initDayJsDate(new Date()).toDate(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [viewCode],
  );

  return (params: AppointmentsPageQueryParams) => {
    const whereClauses: ScheduleWhereInput[] = [];

    if (params['view-code'] === 'upcoming' && params.q?.advanced) {
      const { advanced } = params.q;

      if (!advanced.status || !advanced.date_from) {
        return undefined;
      }

      whereClauses.push({
        startAt: {
          _gte: nowDate,
        },
        status: {
          _in: advanced.status.map((status) => AppointmentStatusResolver[status]),
        },
      });

      whereClauses.push({
        startAt: {
          _gte: startOfDay(parseISO(advanced.date_from)),
        },
        isAllDay: {
          _eq: true,
        },
        status: {
          _in: advanced.status.map((status) => AppointmentStatusResolver[status]),
        },
      });

      return { _or: whereClauses };
    }

    if (params['view-code'] === 'past' && params.q?.advanced) {
      const { advanced } = params.q;

      if (!advanced.date_to) {
        return undefined;
      }

      whereClauses.push({
        startAt: {
          _lt: nowDate,
        },
        isAllDay: {
          _eq: false,
        },
      });

      whereClauses.push({
        startAt: {
          _lt: startOfDay(parseISO(advanced.date_to)),
        },
        isAllDay: {
          _eq: true,
        },
      });

      return { _or: whereClauses };
    }

    if (params['view-code'] === 'overdue' && params.q?.advanced) {
      const { advanced } = params.q;

      if (!advanced.status || !advanced.date_to) {
        return undefined;
      }

      whereClauses.push({
        startAt: {
          _lte: parseISO(advanced.date_to),
        },
        status: {
          _in: advanced.status.map((status) => AppointmentStatusResolver[status]),
        },
      });

      return { _or: whereClauses };
    }

    if (params.q?.advanced) {
      const { advanced } = params.q;

      if (advanced.date_from) {
        whereClauses.push({
          startAt: {
            _gte: initDayJsDate(parseISO(advanced.date_from)).toDate(),
          },
        });
      }

      if (advanced.date_to) {
        whereClauses.push({
          startAt: {
            _lte: initDayJsDate(parseISO(advanced.date_to)).toDate(),
          },
        });
      }

      if (advanced.title) {
        whereClauses.push({
          title: {
            _contains: advanced.title,
          },
        });
      }

      if (advanced.status) {
        whereClauses.push({
          status: {
            _in: advanced.status.map((status) => AppointmentStatusResolver[status]),
          },
        });
      }

      if (advanced.priority) {
        whereClauses.push({
          priority: {
            _eq: advanced.priority as SchedulePriority,
          },
        });
      }

      if (advanced.agent) {
        whereClauses.push({
          user: {
            id: {
              _eq: parseInt(advanced.agent),
            },
          },
        });
      }

      if (advanced.group) {
        whereClauses.push({
          user: {
            groupRelation: {
              id: {
                _eq: parseInt(advanced.group),
              },
            },
          },
        });
      }

      if (advanced.created_by) {
        const createdBy = parseInt(advanced.created_by, 10);

        if (!Number.isNaN(createdBy)) {
          whereClauses.push({
            createdBy: {
              id: {
                _eq: parseInt(advanced.created_by),
              },
            },
          });
        }
      }
    }

    return whereClauses.length ? { _and: whereClauses } : undefined;
  };
};

export const useAppointmentsStatusOptions = ({
  includeIcons,
}: {
  includeIcons?: boolean;
}) => {
  const { t } = useTranslation(['appointments']);
  const appointmentStatusOptions: SelectOption<string>[] = [
    {
      label: t('appointments:filters.status.options.scheduled'),
      value: 'SCHEDULED',
      icon: includeIcons ? 'appointmentScheduled' : undefined,
    },
    {
      label: t('appointments:filters.status.options.completed'),
      value: 'COMPLETED',
      icon: includeIcons ? 'appointmentCompleted' : undefined,
    },
    {
      label: t('appointments:filters.status.options.rescheduled'),
      value: 'RESCHEDULED',
      icon: includeIcons ? 'appointmentRescheduled' : undefined,
    },
    {
      label: t('appointments:filters.status.options.cancelled'),
      value: 'CANCELLED',
      icon: includeIcons ? 'appointmentCancelled' : undefined,
    },
    {
      label: t('appointments:filters.status.options.noShow'),
      value: 'NO-SHOW',
      icon: includeIcons ? 'appointmentNoShow' : undefined,
    },
  ];
  return appointmentStatusOptions;
};

export const useAppointmentsPriorityOptions = ({
  includeIcons,
}: {
  includeIcons?: boolean;
}) => {
  const { t } = useTranslation(['appointments']);
  const appointmentPriorityOptions: SelectOption<string>[] = [
    {
      label: t('appointments:filters.priority.options.low'),
      value: 'LOW',
      icon: includeIcons ? 'priorityLow' : undefined,
    },
    {
      label: t('appointments:filters.priority.options.normal'),
      value: 'NORMAL',
      icon: includeIcons ? 'priorityNormal' : undefined,
    },
    {
      label: t('appointments:filters.priority.options.high'),
      value: 'HIGH',
      icon: includeIcons ? 'priorityHigh' : undefined,
    },
  ];
  return appointmentPriorityOptions;
};
